import { supabase } from "@/integrations/supabase/client";

export type ProcessingEventType = "processed" | "failed" | "rejected_non_invoice" | "exported";

interface RecordEventInput {
  userId: string;
  eventType: ProcessingEventType;
  count?: number;
  companyId?: string | null;
}

/**
 * Fire-and-forget telemetry write. Never throws.
 */
export const recordProcessingEvent = async ({
  userId,
  eventType,
  count = 1,
  companyId = null,
}: RecordEventInput): Promise<void> => {
  if (!userId || count <= 0) return;
  try {
    await supabase.from("processing_events").insert({
      user_id: userId,
      event_type: eventType,
      count,
      company_id: companyId,
    });
  } catch {
    // Telemetry must never break the user flow
  }
};

export interface PeriodCounts {
  processed: number;
  failed: number;
}

export interface DashboardMetrics {
  today: PeriodCounts;
  week: PeriodCounts;
  month: PeriodCounts;
  previousMonth: PeriodCounts;
  /** Daily average of processed in current month (over active days). */
  dailyAverageMonth: number;
}

const startOfTodayISO = (): string => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const startOfWeekISO = (): string => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diffToMonday = (day + 6) % 7;
  d.setDate(d.getDate() - diffToMonday);
  return d.toISOString();
};

const startOfMonthISO = (offsetMonths = 0): string => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  d.setMonth(d.getMonth() + offsetMonths);
  return d.toISOString();
};

const aggregate = (rows: { event_type: string; count: number }[]): PeriodCounts => {
  const out: PeriodCounts = { processed: 0, failed: 0 };
  for (const r of rows) {
    if (r.event_type === "processed") out.processed += r.count;
    else if (r.event_type === "failed") out.failed += r.count;
  }
  return out;
};

export const getDashboardMetrics = async (
  userId: string,
  global = false,
): Promise<DashboardMetrics> => {
  const todayISO = startOfTodayISO();
  const weekISO = startOfWeekISO();
  const monthISO = startOfMonthISO(0);
  const prevMonthStartISO = startOfMonthISO(-1);
  const prevMonthEndISO = monthISO;

  let query = supabase
    .from("processing_events")
    .select("event_type, count, occurred_at")
    .gte("occurred_at", prevMonthStartISO);

  if (!global) query = query.eq("user_id", userId);

  const { data, error } = await query;
  const empty: PeriodCounts = { processed: 0, failed: 0 };
  if (error || !data) {
    return { today: empty, week: empty, month: empty, previousMonth: empty, dailyAverageMonth: 0 };
  }

  const today = aggregate(data.filter((r) => r.occurred_at >= todayISO));
  const week = aggregate(data.filter((r) => r.occurred_at >= weekISO));
  const monthRows = data.filter((r) => r.occurred_at >= monthISO);
  const month = aggregate(monthRows);
  const previousMonth = aggregate(
    data.filter((r) => r.occurred_at >= prevMonthStartISO && r.occurred_at < prevMonthEndISO),
  );

  const activeDays = new Set(
    monthRows
      .filter((r) => r.event_type === "processed")
      .map((r) => r.occurred_at.slice(0, 10)),
  ).size;
  const dailyAverageMonth = activeDays > 0 ? Math.round(month.processed / activeDays) : 0;

  return { today, week, month, previousMonth, dailyAverageMonth };
};

export interface UserActivityRow {
  userId: string;
  email: string;
  fullName: string;
  processed: number;
  failed: number;
  successRate: number;
  dailyAverage: number;
  distinctCompanies: number;
  lastActivity: string | null;
}

export const getUserActivity = async (days = 30): Promise<UserActivityRow[]> => {
  const since = new Date();
  since.setDate(since.getDate() - days);
  const sinceISO = since.toISOString();

  const { data: events, error } = await supabase
    .from("processing_events")
    .select("user_id, event_type, count, occurred_at, company_id")
    .gte("occurred_at", sinceISO);

  if (error || !events) return [];

  const userIds = Array.from(new Set(events.map((e) => e.user_id)));
  if (userIds.length === 0) return [];

  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, email, full_name")
    .in("id", userIds);

  const profileMap = new Map<string, { email: string; full_name: string }>();
  for (const p of profiles ?? []) {
    profileMap.set(p.id, { email: p.email ?? "", full_name: p.full_name ?? "" });
  }

  type Bucket = {
    processed: number;
    failed: number;
    lastAt: string | null;
    activeDays: Set<string>;
    companies: Set<string>;
  };
  const buckets = new Map<string, Bucket>();
  for (const ev of events) {
    let b = buckets.get(ev.user_id);
    if (!b) {
      b = { processed: 0, failed: 0, lastAt: null, activeDays: new Set(), companies: new Set() };
      buckets.set(ev.user_id, b);
    }
    if (ev.event_type === "processed") {
      b.processed += ev.count;
      b.activeDays.add(ev.occurred_at.slice(0, 10));
    } else if (ev.event_type === "failed") {
      b.failed += ev.count;
    }
    if (ev.company_id) b.companies.add(ev.company_id);
    if (!b.lastAt || ev.occurred_at > b.lastAt) b.lastAt = ev.occurred_at;
  }

  const rows: UserActivityRow[] = [];
  for (const [uid, b] of buckets) {
    const profile = profileMap.get(uid) ?? { email: "—", full_name: "" };
    const total = b.processed + b.failed;
    const activeDays = b.activeDays.size;
    rows.push({
      userId: uid,
      email: profile.email,
      fullName: profile.full_name,
      processed: b.processed,
      failed: b.failed,
      successRate: total > 0 ? b.processed / total : 1,
      dailyAverage: activeDays > 0 ? Math.round(b.processed / activeDays) : 0,
      distinctCompanies: b.companies.size,
      lastActivity: b.lastAt,
    });
  }

  rows.sort((a, b) => b.processed - a.processed);
  return rows;
};

// ─────────────────────────────────────────────────────────
// Monthly matrix (user × month)
// ─────────────────────────────────────────────────────────

export interface MonthlyMatrixCell {
  userId: string;
  fullName: string;
  email: string;
  byMonth: Record<string, number>; // "YYYY-MM" → processed
  total: number;
  average: number;
}

export interface MonthlyMatrix {
  months: string[]; // ["YYYY-MM", ...] chronological
  rows: MonthlyMatrixCell[];
  totalsByMonth: Record<string, number>;
  grandTotal: number;
}

const monthKey = (iso: string) => iso.slice(0, 7); // YYYY-MM

const listMonthKeys = (nMonths: number): string[] => {
  const out: string[] = [];
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  for (let i = nMonths - 1; i >= 0; i--) {
    const c = new Date(d);
    c.setMonth(c.getMonth() - i);
    out.push(`${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

export const getMonthlyMatrix = async (months = 6): Promise<MonthlyMatrix> => {
  const monthKeys = listMonthKeys(months);
  const since = new Date();
  since.setDate(1);
  since.setHours(0, 0, 0, 0);
  since.setMonth(since.getMonth() - (months - 1));
  const sinceISO = since.toISOString();

  const { data: events, error } = await supabase
    .from("processing_events")
    .select("user_id, count, occurred_at, event_type")
    .eq("event_type", "processed")
    .gte("occurred_at", sinceISO);

  if (error || !events) {
    return { months: monthKeys, rows: [], totalsByMonth: {}, grandTotal: 0 };
  }

  const userIds = Array.from(new Set(events.map((e) => e.user_id)));
  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, email, full_name")
    .in("id", userIds);
  const profileMap = new Map<string, { email: string; full_name: string }>();
  for (const p of profiles ?? []) {
    profileMap.set(p.id, { email: p.email ?? "", full_name: p.full_name ?? "" });
  }

  const rowMap = new Map<string, MonthlyMatrixCell>();
  const totalsByMonth: Record<string, number> = {};
  monthKeys.forEach((m) => (totalsByMonth[m] = 0));

  for (const ev of events) {
    const mk = monthKey(ev.occurred_at);
    if (!monthKeys.includes(mk)) continue;
    let row = rowMap.get(ev.user_id);
    if (!row) {
      const p = profileMap.get(ev.user_id) ?? { email: "—", full_name: "" };
      const byMonth: Record<string, number> = {};
      monthKeys.forEach((m) => (byMonth[m] = 0));
      row = {
        userId: ev.user_id,
        fullName: p.full_name,
        email: p.email,
        byMonth,
        total: 0,
        average: 0,
      };
      rowMap.set(ev.user_id, row);
    }
    row.byMonth[mk] += ev.count;
    row.total += ev.count;
    totalsByMonth[mk] += ev.count;
  }

  const rows = Array.from(rowMap.values()).map((r) => ({
    ...r,
    average: Math.round(r.total / monthKeys.length),
  }));
  rows.sort((a, b) => b.total - a.total);

  const grandTotal = Object.values(totalsByMonth).reduce((a, b) => a + b, 0);
  return { months: monthKeys, rows, totalsByMonth, grandTotal };
};

// ─────────────────────────────────────────────────────────
// Global monthly series (last N months)
// ─────────────────────────────────────────────────────────

export interface MonthlySeriesPoint {
  month: string; // "YYYY-MM"
  label: string; // "abr 2026"
  processed: number;
  failed: number;
  failedPct: number; // 0..100
}

const MONTH_LABELS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const monthLabel = (mk: string) => {
  const [y, m] = mk.split("-");
  return `${MONTH_LABELS[Number(m) - 1]} ${y}`;
};

export const getGlobalMonthlySeries = async (months = 12): Promise<MonthlySeriesPoint[]> => {
  const monthKeys = listMonthKeys(months);
  const since = new Date();
  since.setDate(1);
  since.setHours(0, 0, 0, 0);
  since.setMonth(since.getMonth() - (months - 1));
  const sinceISO = since.toISOString();

  const { data: events, error } = await supabase
    .from("processing_events")
    .select("count, occurred_at, event_type")
    .gte("occurred_at", sinceISO);

  const bucket: Record<string, { processed: number; failed: number }> = {};
  monthKeys.forEach((m) => (bucket[m] = { processed: 0, failed: 0 }));

  if (!error && events) {
    for (const ev of events) {
      const mk = monthKey(ev.occurred_at);
      if (!(mk in bucket)) continue;
      if (ev.event_type === "processed") bucket[mk].processed += ev.count;
      else if (ev.event_type === "failed") bucket[mk].failed += ev.count;
    }
  }

  return monthKeys.map((mk) => {
    const b = bucket[mk];
    const total = b.processed + b.failed;
    return {
      month: mk,
      label: monthLabel(mk),
      processed: b.processed,
      failed: b.failed,
      failedPct: total > 0 ? +((b.failed / total) * 100).toFixed(1) : 0,
    };
  });
};

// ─────────────────────────────────────────────────────────
// Top companies (current month)
// ─────────────────────────────────────────────────────────

export interface CompanyUsageRow {
  companyId: string;
  name: string;
  count: number;
  share: number; // 0..1
}

export const getTopCompanies = async (limit = 10): Promise<CompanyUsageRow[]> => {
  const monthISO = startOfMonthISO(0);
  const { data: events, error } = await supabase
    .from("processing_events")
    .select("company_id, count, event_type")
    .eq("event_type", "processed")
    .gte("occurred_at", monthISO);

  if (error || !events) return [];

  const totals = new Map<string, number>();
  let grand = 0;
  for (const ev of events) {
    if (!ev.company_id) continue;
    totals.set(ev.company_id, (totals.get(ev.company_id) ?? 0) + ev.count);
    grand += ev.count;
  }

  const ids = Array.from(totals.keys());
  if (ids.length === 0) return [];
  const { data: companies } = await supabase
    .from("company_masters")
    .select("id, name")
    .in("id", ids);
  const nameMap = new Map<string, string>();
  for (const c of companies ?? []) nameMap.set(c.id, c.name ?? "—");

  const rows: CompanyUsageRow[] = Array.from(totals.entries()).map(([id, count]) => ({
    companyId: id,
    name: nameMap.get(id) ?? "—",
    count,
    share: grand > 0 ? count / grand : 0,
  }));
  rows.sort((a, b) => b.count - a.count);
  return rows.slice(0, limit);
};

// ─────────────────────────────────────────────────────────
// User personal monthly series
// ─────────────────────────────────────────────────────────

export const getUserMonthlySeries = async (
  userId: string,
  months = 6,
): Promise<MonthlySeriesPoint[]> => {
  const monthKeys = listMonthKeys(months);
  const since = new Date();
  since.setDate(1);
  since.setHours(0, 0, 0, 0);
  since.setMonth(since.getMonth() - (months - 1));
  const sinceISO = since.toISOString();

  const { data: events, error } = await supabase
    .from("processing_events")
    .select("count, occurred_at, event_type")
    .eq("user_id", userId)
    .gte("occurred_at", sinceISO);

  const bucket: Record<string, { processed: number; failed: number }> = {};
  monthKeys.forEach((m) => (bucket[m] = { processed: 0, failed: 0 }));

  if (!error && events) {
    for (const ev of events) {
      const mk = monthKey(ev.occurred_at);
      if (!(mk in bucket)) continue;
      if (ev.event_type === "processed") bucket[mk].processed += ev.count;
      else if (ev.event_type === "failed") bucket[mk].failed += ev.count;
    }
  }

  return monthKeys.map((mk) => {
    const b = bucket[mk];
    const total = b.processed + b.failed;
    return {
      month: mk,
      label: monthLabel(mk),
      processed: b.processed,
      failed: b.failed,
      failedPct: total > 0 ? +((b.failed / total) * 100).toFixed(1) : 0,
    };
  });
};
